import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BrandMark } from '../brand/BrandMark';
import { faChevronDown, faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import {
  groupFor,
  systemFor,
  visibleEntries,
  visibleSystems,
  type NavEntry,
  type NavSystem,
} from './navigation';
import type { RoleName } from '../../types/api';

interface SidebarProps {
  role: RoleName;
  /** Solo el riel de sistemas, sin el panel de la aplicacion. */
  isCollapsed: boolean;
  /** Cierra el panel deslizante al navegar. Sin efecto en escritorio. */
  onNavigate?: () => void;
  /** Repliega el sidebar al riel. En movil cierra el panel deslizante. */
  onCollapse?: () => void;
}

const linkBase =
  'flex items-center gap-3 rounded-card px-3 py-2.5 text-sm font-medium transition-colors';

function EntryLink({ entry, onNavigate }: { entry: NavEntry; onNavigate?: () => void }): ReactNode {
  if (!entry.enabled) {
    return (
      <span
        title={`${entry.label}: disponible en una fase posterior`}
        aria-disabled="true"
        className={`${linkBase} cursor-not-allowed text-white/30`}
      >
        <FontAwesomeIcon icon={entry.icon} aria-hidden="true" className="w-4 shrink-0" />
        <span className="truncate">{entry.label}</span>
      </span>
    );
  }

  return (
    <NavLink
      to={entry.to}
      onClick={onNavigate}
      className={({ isActive }: { isActive: boolean }) =>
        [
          linkBase,
          'relative',
          isActive
            ? 'bg-white/12 text-white before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-1 before:rounded-full before:bg-accent'
            : 'text-white/70 hover:bg-white/8 hover:text-white',
        ].join(' ')
      }
    >
      <FontAwesomeIcon icon={entry.icon} aria-hidden="true" className="w-4 shrink-0" />
      <span className="truncate">{entry.label}</span>
    </NavLink>
  );
}

/**
 * Riel de sistemas: una franja de iconos, uno por aplicacion.
 *
 * Es el unico nivel que sobrevive al modo compacto, porque cambiar de
 * aplicacion tiene que estar siempre a un clic. El sistema seleccionado se
 * marca con la barra de acento, igual que la pantalla activa dentro del panel.
 */
function SystemRail({
  systems,
  selectedId,
  onSelect,
}: {
  systems: NavSystem[];
  selectedId: string | null;
  onSelect: (system: NavSystem) => void;
}): ReactNode {
  return (
    <div
      aria-label="Aplicaciones"
      role="tablist"
      aria-orientation="vertical"
      className="flex w-14 shrink-0 flex-col items-center gap-1 border-r border-white/10 bg-black/25 py-3"
    >
      {/*
        La marca corona el riel, encima de las aplicaciones. Es el sitio donde
        se la busca y ademas separa lo que identifica al sistema de lo que se
        pulsa para navegar.
      */}
      <BrandMark className="mb-2 h-7 w-auto text-white" />

      {systems.map((system) => {
        const isSelected = system.id === selectedId;

        return (
          <button
            key={system.id}
            type="button"
            role="tab"
            aria-selected={isSelected}
            aria-controls="nav-system-panel"
            disabled={!system.enabled}
            title={system.enabled ? system.label : `${system.label}: aún no existe`}
            onClick={() => {
              onSelect(system);
            }}
            className={[
              'relative grid h-11 w-11 place-items-center rounded-card transition-colors',
              !system.enabled
                ? 'cursor-not-allowed text-white/25'
                : isSelected
                  ? 'bg-white/12 text-white before:absolute before:left-0 before:top-2 before:bottom-2 before:w-1 before:rounded-full before:bg-accent'
                  : 'text-white/55 hover:bg-white/8 hover:text-white',
            ].join(' ')}
          >
            <FontAwesomeIcon icon={system.icon} aria-hidden="true" />
            <span className="sr-only">{system.label}</span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * Sidebar del panel: riel de aplicaciones a la izquierda y, a su derecha, las
 * pantallas de la aplicacion seleccionada.
 *
 * Separar las dos columnas es lo que permite que el panel aloje varias
 * aplicaciones sin que la navegacion se convierta en una lista de treinta
 * enlaces: en el panel solo esta lo de una aplicacion, y el riel dice cuantas
 * hay y en cual estas.
 *
 * Seleccionar un sistema en el riel cambia lo que muestra el panel y no navega
 * a ningun sitio. Mirar que tiene otra aplicacion no deberia sacarte de la
 * pantalla en la que estas trabajando; se navega al pulsar una entrada.
 *
 * El grupo que contiene la ruta activa se abre solo, y las entradas de fases
 * futuras se muestran deshabilitadas en lugar de ocultarse, para que el
 * alcance del sistema sea visible desde el principio.
 */
export function Sidebar({ role, isCollapsed, onNavigate, onCollapse }: SidebarProps): ReactNode {
  const location = useLocation();
  const systems = visibleSystems(role);
  const activeSystem = systemFor(location.pathname);
  const activeGroupId = groupFor(location.pathname)?.id ?? null;

  const [selectedId, setSelectedId] = useState<string | null>(
    () => activeSystem?.id ?? systems.find((system) => system.enabled)?.id ?? null,
  );
  const [openGroups, setOpenGroups] = useState<string[]>(() =>
    activeGroupId ? [activeGroupId] : ['warehouse-catalogs'],
  );

  // Navegar devuelve el panel a la aplicacion de la ruta: si estabas mirando
  // otra y pulsas atras, el panel no puede quedarse en la que no es.
  useEffect(() => {
    if (activeSystem) {
      setSelectedId(activeSystem.id);
    }
  }, [activeSystem]);

  useEffect(() => {
    if (activeGroupId) {
      setOpenGroups((current) =>
        current.includes(activeGroupId) ? current : [...current, activeGroupId],
      );
    }
  }, [activeGroupId]);

  const selected = systems.find((system) => system.id === selectedId) ?? null;
  const entries = selected ? visibleEntries(selected.entries, role) : [];
  const groups = (selected?.groups ?? [])
    .map((group) => ({ group, entries: visibleEntries(group.entries, role) }))
    .filter((item) => item.entries.length > 0);

  return (
    <nav aria-label="Navegacion principal" className="flex h-full bg-primary text-white">
      <SystemRail
        systems={systems}
        selectedId={selectedId}
        onSelect={(system) => {
          setSelectedId(system.id);
        }}
      />

      {isCollapsed || !selected ? null : (
        <div
          id="nav-system-panel"
          role="tabpanel"
          aria-label={selected.label}
          className="flex min-w-0 flex-1 flex-col overflow-y-auto px-2 py-3"
        >
          <div className="mb-2 flex items-center gap-1 px-1">
            {onCollapse ? (
              <button
                type="button"
                onClick={onCollapse}
                aria-label="Replegar la navegacion"
                className="grid h-8 w-8 shrink-0 place-items-center rounded-card text-white/50 hover:bg-white/8 hover:text-white"
              >
                <FontAwesomeIcon icon={faChevronLeft} aria-hidden="true" className="w-3" />
              </button>
            ) : null}
            <p className="truncate text-xs font-semibold uppercase tracking-wider text-white/80">
              {selected.label}
            </p>
          </div>

          <ul className="space-y-1">
            {entries.map((entry) => (
              <li key={entry.to}>
                <EntryLink entry={entry} onNavigate={onNavigate} />
              </li>
            ))}
          </ul>

          <div className="mt-1 space-y-1">
            {groups.map(({ group, entries: groupEntries }) => {
              const isOpen = openGroups.includes(group.id);

              return (
                <div key={group.id}>
                  <button
                    type="button"
                    aria-expanded={isOpen}
                    aria-controls={`nav-group-${group.id}`}
                    onClick={() => {
                      setOpenGroups((current) =>
                        current.includes(group.id)
                          ? current.filter((other) => other !== group.id)
                          : [...current, group.id],
                      );
                    }}
                    className="flex w-full items-center justify-between rounded-card px-3 py-2 text-xs font-semibold uppercase tracking-wide text-white/45 hover:bg-white/8 hover:text-white/80"
                  >
                    <span className="truncate">{group.label}</span>
                    <FontAwesomeIcon
                      icon={faChevronDown}
                      aria-hidden="true"
                      className={`w-3 shrink-0 transition-transform ${isOpen ? '' : '-rotate-90'}`}
                    />
                  </button>

                  {isOpen ? (
                    <ul id={`nav-group-${group.id}`} className="space-y-1">
                      {groupEntries.map((entry) => (
                        <li key={entry.to}>
                          <EntryLink entry={entry} onNavigate={onNavigate} />
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              );
            })}
          </div>

          <div className="mt-auto pt-4 text-xs text-white/40">
            <p className="truncate">Inventario y Logística</p>
            <p>v0.2</p>
          </div>
        </div>
      )}
    </nav>
  );
}
